import type {
  WorkflowBlockDefinition,
  WorkflowBuilderData,
  WorkflowData,
  WorkflowGraph,
  WorkflowGraphConnection,
  WorkflowGraphNode,
  WorkflowValidationResult,
} from "@/types/leadflow"

export const WORKFLOW_EXPORT_FORMAT = "leadflow_workflow"
export const WORKFLOW_EXPORT_FORMAT_VERSION = 1
export const REDACTED_VALUE = "[REDACTED]"

const SENSITIVE_CONFIG_KEY_PATTERN = /(?:secret|token|password|authorization|api[_-]?key|apikey|credential|private[_-]?key|webhook[_-]?secret)/i

export type WorkflowExportPayload = {
  format: typeof WORKFLOW_EXPORT_FORMAT
  format_version: typeof WORKFLOW_EXPORT_FORMAT_VERSION
  exported_at: string
  workflow: {
    name: string
    description: string | null
    status: string
    schema_version: number
    version: number | null
    published_version: number | null
    has_unpublished_changes: boolean | null
    validation: {
      valid: boolean | null
      errors: string[]
      warnings: string[]
      summary: string | null
    }
    nodes: WorkflowExportNode[]
    connections: WorkflowExportConnection[]
  }
}

export type WorkflowExportNode = {
  key: string
  internal_graph_node_id?: string
  type: string
  category: string
  label: string
  position: { x: number; y: number }
  config: unknown
  runtime_status: "active" | "internal_only" | "external" | "inactive" | "future" | "unsupported"
  inputs?: Array<{ key: string; label: string; required: boolean; multiple: boolean }>
  outputs?: Array<{ key: string; label: string; required: boolean; multiple: boolean }>
}

export type WorkflowExportConnection = {
  key: string
  internal_graph_connection_id?: string
  source: string
  source_port: string
  target: string
  target_port: string
  branch?: string
}

export function buildWorkflowExport({
  workflow,
  workflowGraph,
  graph,
  blocks,
  validation,
  hasUnpublishedChanges,
  exportedAt = new Date().toISOString(),
}: {
  workflow: Pick<WorkflowData, "name" | "description" | "status" | "version" | "published_version"> | null
  workflowGraph?: Pick<WorkflowBuilderData, "status" | "version" | "published_version" | "graph_draft_different_from_published"> | null
  graph: WorkflowGraph
  blocks: WorkflowBlockDefinition[]
  validation?: WorkflowValidationResult | null
  hasUnpublishedChanges?: boolean | null
  exportedAt?: string
}): WorkflowExportPayload {
  const blockMap = new Map(blocks.map((block) => [block.type, block]))
  const nodeKeyMap = buildPortableNodeKeyMap(graph.nodes)

  return {
    format: WORKFLOW_EXPORT_FORMAT,
    format_version: WORKFLOW_EXPORT_FORMAT_VERSION,
    exported_at: exportedAt,
    workflow: {
      name: normalizeWorkflowName(workflow?.name),
      description: workflow?.description ?? null,
      status: workflowGraph?.status ?? workflow?.status ?? "draft",
      schema_version: normalizeSchemaVersion(graph.schema_version),
      version: normalizeNullableNumber(workflowGraph?.version ?? workflow?.version ?? null),
      published_version: normalizeNullableNumber(workflowGraph?.published_version ?? workflow?.published_version ?? null),
      has_unpublished_changes: typeof hasUnpublishedChanges === "boolean"
        ? hasUnpublishedChanges
        : typeof workflowGraph?.graph_draft_different_from_published === "boolean"
          ? workflowGraph.graph_draft_different_from_published
          : null,
      validation: {
        valid: validation ? validation.valid : null,
        errors: validation?.errors ?? [],
        warnings: validation?.warnings ?? [],
        summary: validation?.summary ?? null,
      },
      nodes: graph.nodes.map((node) => buildExportNode(node, nodeKeyMap, blockMap)),
      connections: graph.connections
        .map((connection, index) => buildExportConnection(connection, index, nodeKeyMap))
        .filter((connection): connection is WorkflowExportConnection => connection !== null),
    },
  }
}

export function sanitizeWorkflowFilename(name: string | null | undefined) {
  const base = normalizeWorkflowName(name)
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120)

  return `${base || "untitled-workflow"}.workflow.json`
}

export function redactSensitiveConfig(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => redactSensitiveConfig(item))
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [
      key,
      isSensitiveConfigKey(key) ? REDACTED_VALUE : redactSensitiveConfig(nestedValue),
    ])
  )
}

export function downloadWorkflowExport(payload: WorkflowExportPayload, filename: string) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  anchor.rel = "noopener"
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

function buildExportNode(
  node: WorkflowGraphNode,
  nodeKeyMap: Map<string, string>,
  blockMap: Map<string, WorkflowBlockDefinition>
): WorkflowExportNode {
  const block = blockMap.get(node.type)
  return {
    key: nodeKeyMap.get(node.id) ?? portableKeyFromId(node.id),
    internal_graph_node_id: node.id,
    type: node.type,
    category: block?.category ?? "unknown",
    label: block?.display_name ?? humanizeType(node.type),
    position: {
      x: normalizeNumber(node.position?.x),
      y: normalizeNumber(node.position?.y),
    },
    config: redactSensitiveConfig(node.config ?? {}),
    runtime_status: runtimeStatusForBlock(block),
    inputs: block?.inputs?.map((port) => ({ ...port })),
    outputs: block?.outputs?.map((port) => ({ ...port })),
  }
}

function buildExportConnection(
  connection: WorkflowGraphConnection,
  index: number,
  nodeKeyMap: Map<string, string>
): WorkflowExportConnection | null {
  const source = nodeKeyMap.get(connection.sourceNodeId)
  const target = nodeKeyMap.get(connection.targetNodeId)
  if (!source || !target) return null

  const branch = branchMeaning(connection.sourcePort)
  return {
    key: `connection_${index + 1}`,
    internal_graph_connection_id: connection.id,
    source,
    source_port: connection.sourcePort,
    target,
    target_port: connection.targetPort,
    ...(branch ? { branch } : {}),
  }
}

function buildPortableNodeKeyMap(nodes: WorkflowGraphNode[]) {
  const used = new Set<string>()
  return new Map(nodes.map((node, index) => {
    const readable = portableKeyFromId(node.id)
    const key = readable && !used.has(readable) ? readable : `node_${index + 1}`
    used.add(key)
    return [node.id, key]
  }))
}

function portableKeyFromId(id: string) {
  const normalized = String(id || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
  return normalized ? `node_${normalized}` : ""
}

function runtimeStatusForBlock(block: WorkflowBlockDefinition | undefined): WorkflowExportNode["runtime_status"] {
  if (!block) return "unsupported"
  if (block.draft_only) return "future"
  if (!block.runtime_available) return "future"
  if (block.type === "sms.send" || block.type === "webhook.call") return "future"
  if (block.type === "email.send") return "external"
  if (block.type.includes("schedule")) return "inactive"
  if (block.category === "action") return "internal_only"
  return "active"
}

function branchMeaning(sourcePort: string) {
  const port = String(sourcePort || "").toLowerCase()
  if (port === "else") return "fail"
  if (["true", "false", "yes", "no"].includes(port)) return port
  return undefined
}

function isSensitiveConfigKey(key: string) {
  return SENSITIVE_CONFIG_KEY_PATTERN.test(key)
}

function normalizeWorkflowName(name: string | null | undefined) {
  const trimmed = String(name ?? "").trim()
  return trimmed || "Untitled Workflow"
}

function humanizeType(type: string) {
  return String(type || "Unknown block")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function normalizeSchemaVersion(value: unknown) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1
}

function normalizeNullableNumber(value: unknown) {
  if (value == null) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function normalizeNumber(value: unknown) {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value)
}
